import { Link } from 'react-router-dom';
import { useAuth } from '@core/auth/useAuth';
import { useBillingBalance } from '@core/billing/useBillingBalance';

/**
 * Sidebar trial / usage card (Phase 8 §3 F · C-12).
 * Fixed 96 px height, mounts only when the sidebar is expanded.
 *
 * Now wired to `useBillingBalance()`:
 *   - Hides completely when the trial is not active (no daysLeft to show).
 *   - Renders a usage meter derived from the 14-day default trial window.
 *   - Graceful fallback: on error the card shows "—" rather than crashing.
 */

/** Assumed total trial window length for the usage meter (days). */
const TRIAL_WINDOW_DAYS = 14;

export function SidebarTrialCard() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? undefined;
  const { balance, daysLeft, isTrialActive, isLoading, isError } = useBillingBalance(orgId);

  // Spec: hide the card entirely when there is no active trial.
  // We still mount a placeholder while loading so we don't flash-then-hide.
  if (!isLoading && !isError && !isTrialActive) {
    return null;
  }

  const progress = isError || isLoading
    ? 0
    : Math.max(0, Math.min(1, (TRIAL_WINDOW_DAYS - daysLeft) / TRIAL_WINDOW_DAYS));

  const headline = isError
    ? 'Trial status unavailable'
    : isLoading
      ? 'Loading trial…'
      : 'Your free trial is active';

  const detail = isError
    ? '—'
    : isLoading
      ? ' '
      : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left · ${balance.toLocaleString()} credits`;

  return (
    <div
      className="mx-3 mb-2 rounded-[10px] px-3 py-3 flex flex-col justify-between"
      style={{
        height: 96,
        backgroundColor: 'var(--ggj-accent-soft)',
        color: 'var(--ggj-sidebar-text-active)',
      }}
    >
      <div className="min-w-0">
        <p
          className="text-[13px] font-semibold leading-tight truncate"
          style={{ color: 'var(--ggj-accent-hover)' }}
        >
          {headline}
        </p>
        <p
          className="mt-1 text-[11px] leading-snug line-clamp-2"
          style={{ color: 'var(--ggj-accent-hover)', opacity: 0.85 }}
        >
          {detail}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <div
          className="h-1.5 flex-1 rounded-full"
          style={{ backgroundColor: 'rgba(16,185,129,0.18)' }}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          aria-label="Trial usage"
        >
          <div
            className="h-full rounded-full transition-[width]"
            style={{
              width: `${progress * 100}%`,
              backgroundColor: 'var(--ggj-accent)',
            }}
          />
        </div>
        <Link
          to="/settings/billing"
          className="text-[11px] font-medium whitespace-nowrap hover:underline"
          style={{ color: 'var(--ggj-accent-hover)' }}
        >
          Upgrade
        </Link>
      </div>
    </div>
  );
}
